import { useState } from 'react';
import { Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { lookupCustomerByDocument } from '../../../../../services/PublicOrderService';

const formatCpf = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const StepCustomerInfo = ({ companyUuid, customerData, onSubmit, onBack }) => {
    const { t } = useTranslation();

    const splitCustomerName = (customer) => {
        const fullName = customer?.fullName || `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim();
        const parts = fullName.split(/\s+/).filter(Boolean);
        return {
            firstName: customer?.firstName || parts[0] || '',
            lastName: customer?.lastName || parts.slice(1).join(' ')
        };
    };

    // Phase: 'cpf' | 'form'
    const [phase, setPhase] = useState(customerData?.document ? 'form' : 'cpf');
    const [cpf, setCpf] = useState(customerData?.document ? formatCpf(customerData.document) : '');
    const [lookingUp, setLookingUp] = useState(false);
    const [lookupError, setLookupError] = useState('');

    const [formData, setFormData] = useState({
        firstName: customerData?.firstName || '',
        lastName: customerData?.lastName || '',
        phone: customerData?.phone || '',
        document: customerData?.document || ''
    });
    const [errors, setErrors] = useState({});

    const handleCpfChange = (e) => {
        setCpf(formatCpf(e.target.value));
        setLookupError('');
    };

    const handleCpfSubmit = async (e) => {
        e.preventDefault();
        const digits = cpf.replace(/\D/g, '');
        if (digits.length !== 11) {
            setLookupError(t('validation.invalid.cpf'));
            return;
        }

        try {
            setLookingUp(true);
            setLookupError('');
            const response = await lookupCustomerByDocument(companyUuid, digits);
            const customer = response.data;
            const { firstName, lastName } = splitCustomerName(customer);
            setFormData({
                firstName,
                lastName,
                phone: customer.phone || '',
                document: digits
            });
            setPhase('form');
        } catch (err) {
            if (err.response?.status === 404) {
                setFormData(prev => ({ ...prev, document: digits, firstName: '', lastName: '', phone: '' }));
                setPhase('form');
            } else {
                setLookupError(t('booking.lookup_error'));
            }
        } finally {
            setLookingUp(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = t('validation.required.firstName');
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = t('validation.min.generic', { field: t('firstName'), qty: 2 });
        }

        if (formData.lastName.trim() && formData.lastName.trim().length < 2) {
            newErrors.lastName = t('validation.min.generic', { field: t('lastName'), qty: 2 });
        }

        if (!formData.phone.trim()) {
            newErrors.phone = t('validation.required.phone');
        } else if (formData.phone.replace(/\D/g, '').length < 10) {
            newErrors.phone = t('validation.invalid.phone');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSubmit({ ...formData });
        }
    };

    // ── Phase: enter CPF ──────────────────────────────────────────────────
    if (phase === 'cpf') {
        return (
            <div className="step-customer">
                <div className="step-header mb-4">
                    <h4 className="step-title">
                        <i className="fa fa-id-card me-2 text-primary"></i>
                        {t('booking.enter_cpf_title')}
                    </h4>
                    <p className="step-subtitle text-muted">
                        {t('booking.enter_cpf_description')}
                    </p>
                </div>

                <Form onSubmit={handleCpfSubmit}>
                    <Row className="g-3 justify-content-center">
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>CPF <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={cpf}
                                    onChange={handleCpfChange}
                                    placeholder="000.000.000-00"
                                    className="form-control-lg text-center"
                                    inputMode="numeric"
                                    autoFocus
                                />
                                {lookupError && (
                                    <Alert variant="danger" className="mt-2 py-2">
                                        {lookupError}
                                    </Alert>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <div className="step-actions d-flex justify-content-between mt-4">
                        <Button variant="outline-secondary" type="button" onClick={onBack}>
                            <i className="fa fa-arrow-left me-2"></i>
                            {t('back')}
                        </Button>
                        <Button variant="primary" type="submit" disabled={lookingUp}>
                            {lookingUp
                                ? <><Spinner size="sm" className="me-2" />{t('booking.searching')}</>
                                : <>{t('continue')}<i className="fa fa-arrow-right ms-2"></i></>
                            }
                        </Button>
                    </div>
                </Form>
            </div>
        );
    }

    // ── Phase: fill / confirm details ─────────────────────────────────────
    return (
        <div className="step-customer">
            <div className="step-header mb-4">
                <h4 className="step-title">
                    <i className="fa fa-user me-2 text-primary"></i>
                    {t('booking.your_info')}
                </h4>
                <p className="step-subtitle text-muted">
                    {t('booking.your_info_description')}
                </p>
            </div>

            <Form onSubmit={handleFormSubmit}>
                <Row className="g-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>{t('firstName')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder={t('firstName')}
                                isInvalid={!!errors.firstName}
                                className="form-control-lg"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.firstName}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>{t('lastName')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                placeholder={t('lastName')}
                                isInvalid={!!errors.lastName}
                                className="form-control-lg"
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.lastName}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="g-3 mt-1">
                    <Col md={12}>
                        <Form.Group>
                            <Form.Label>{t('phone')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="(00) 00000-0000"
                                isInvalid={!!errors.phone}
                                className="form-control-lg"
                            />
                            {errors.phone && (
                                <div className="invalid-feedback d-block">
                                    {errors.phone}
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <div className="step-actions d-flex justify-content-between mt-4">
                    <Button variant="outline-secondary" type="button" onClick={() => setPhase('cpf')}>
                        <i className="fa fa-arrow-left me-2"></i>
                        {t('back')}
                    </Button>
                    <Button variant="primary" type="submit">
                        Revisar pedido
                        <i className="fa fa-arrow-right ms-2"></i>
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default StepCustomerInfo;
